import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import type React from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, type To, useNavigate } from 'react-router-dom';
import type { z } from 'zod';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import {
  generateVerificationInformationSupportingInformationSchema,
  supportingInformationToVerificationInformationSupportingInformationAnswers,
  type VerificationInformationSupportingInformationOrDefaults,
  verificationInformationSupportingInformationAnswersToSupportingInformation,
} from '@/features/verification/schemas/supporting-information';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import {
  useProfileVerificationInformationSupportingInformation,
  useProfileVerificationInformationSupportingInformationMutation,
} from '@/hooks/queries/use-profile-verification-information-supporting-information';
import { VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_ANSWERS, type VerificationInformationSupportingInformationField, WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import type { DefinedPartial } from '@/types/generics';

type VerificationSupportingInformationProps = {
  next: To;
  prev: To;
};

const VerificationSupportingInformationForm = ({
  defaultValues,
  requiredFields,
  onSuccess,
}: {
  defaultValues: VerificationInformationSupportingInformationOrDefaults;
  requiredFields: VerificationInformationSupportingInformationField[];
  onSuccess: VoidFunction;
}) => {
  const schema = generateVerificationInformationSupportingInformationSchema(requiredFields);
  const form = useForm<z.infer<typeof schema>>({
    defaultValues: requiredFields.reduce(
      (acc, field) => {
        acc[field] = defaultValues[field];
        return acc;
      },
      {} as DefinedPartial<z.infer<typeof schema>>,
    ),
    resolver: standardSchemaResolver(schema),
  });
  const { isPending, isSuccess, mutate } = useProfileVerificationInformationSupportingInformationMutation();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: z.infer<typeof schema>) => {
    const answers = supportingInformationToVerificationInformationSupportingInformationAnswers(data);

    mutate(
      {
        answers,
      },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess,
      },
    );
  };

  return (
    <Form {...form}>
      <form name="verification-supporting-information" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-8">
          {requiredFields.map((requiredField) => (
            <FormField
              key={requiredField}
              name={requiredField}
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-4">
                  <FormLabel>{t(`verificationSupportingInformation.questions.${requiredField}.title`)}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={String(field.value)}
                      variant="cards"
                      aria-label={t(`verificationSupportingInformation.questions.${requiredField}.title`)}
                      onValueChange={field.onChange}
                    >
                      {VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_ANSWERS.map((answer) => (
                        <FormItem key={answer} className="flex-1">
                          <RadioGroupItemCard variant="outline">
                            <FormControl>
                              <RadioGroupItem value={answer} />
                            </FormControl>
                            <FormLabel contained>{t(`verificationSupportingInformation.questions.${requiredField}.answers.${answer}`)}</FormLabel>
                          </RadioGroupItemCard>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending || isSuccess}>
            <span className={isPending || isSuccess ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
VerificationSupportingInformationForm.displayName = 'VerificationSupportingInformationForm';

const VerificationSupportingInformationContent = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation();

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('verificationSupportingInformation.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('verificationSupportingInformation.description')}</PageHeadingDescription>
      </PageHeading>
      <PageCard>{children}</PageCard>
    </>
  );
};
VerificationSupportingInformationContent.displayName = 'VerificationSupportingInformationContent';

const VerificationSupportingInformation = ({ next, prev }: VerificationSupportingInformationProps) => {
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const isWorkflowVerificationLevel = !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const {
    isComplete: isCompleteSupportingInformation,
    isLoading: isLoadingSupportingInformation,
    data: supportingInformation,
    refetch: refetchSupportingInformation,
  } = useProfileVerificationInformationSupportingInformation(isWorkflowVerificationLevel);
  const navigate = useNavigate();
  const isLoading = isLoadingVerificationInformation || isLoadingSupportingInformation;

  const handleSuccess = useCallback(async () => {
    await refetchSupportingInformation();
    navigate(next, { replace: true });
  }, [navigate, next, refetchSupportingInformation]);

  if (isLoading) return <LoadingCard />;

  if (!verificationInformation || verificationInformation.verification.status === 'NOT_VERIFIED') return <Navigate to={prev} replace />;

  if (!isWorkflowVerificationLevel || isCompleteSupportingInformation) return <Navigate to={next} replace />;

  if (!supportingInformation) throw new Error('Unable to proceed with supporting information, no supporting information');

  const defaultValues = verificationInformationSupportingInformationAnswersToSupportingInformation(supportingInformation.requiredFields, supportingInformation.answers);

  return (
    <VerificationSupportingInformationContent>
      <VerificationSupportingInformationForm defaultValues={defaultValues} requiredFields={supportingInformation.requiredFields} onSuccess={handleSuccess} />
    </VerificationSupportingInformationContent>
  );
};
VerificationSupportingInformation.displayName = 'VerificationSupportingInformation';

export default VerificationSupportingInformation;
